import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { NullAdsController } from "@mp-commons/modules/ads/AdsController.js";
import { CP_EmbedType } from "@mp-commons/modules/ads/adcall/CustomParams.js";
import { ContentSpecificAdData, ExternallyPassedContentAdData } from "@mp-commons/modules/ads/adcall/composable/ContentSpecificAdCallFilter.js";
import { UserAdsData } from "@mp-commons/modules/ads/adcall/composable/UserAdsData.js";
import { MultipleVASTImaAdCallController, MultipleVASTImaAdCallControllerConfig, VASTAdUnitConfig } from "@mp-commons/modules/ads/ima/MultipleVASTImaAdCallController.js";
import { BaseUnmuter, IMAAdvancedUnmuter, IMAUnmuterConservativeConfig, NoOpUnumuter } from "@mp-commons/modules/ads/ima/UserGestureUnmuter.js";
import { AmazonBidBehaviour } from "@mp-commons/modules/ads/prebid/AmazonHB.js";
import { NullPrebidModule } from "@mp-commons/modules/ads/prebid/NullPrebidModule.js";
import { VideoPolicyRules } from "@mp-commons/modules/ads/vprules/VideoPolicyRules.js";
import { MediaState, MediaStatusSubscription } from "@mp-commons/modules/player/AbstractPlayerControls.js";
import { createSizePreference } from "@mp-commons/modules/player/video/FormatAndSizePreference.js";
import { ExternalCloseUIElement, HasExternalUIElements, VideoPlayerControls } from "@mp-commons/modules/player/Player.js";
import { PreloadManagementStrategy, UnloadManagementStrategy } from "@mp-commons/modules/player/PlayerControlsConfig.js";
import { AudioMode, PlayMode } from "@mp-commons/modules/player/MediaPlayOptions.js";
import { NativeVODLoadStrategy, NativeVODPlayerFactory } from "@mp-commons/modules/player/video/native/NativeVODPlayerFactory.js";
import { StaticPlaylist } from "@mp-commons/modules/playlist/StaticPlaylist.js";
import { dispatchMegarideEvents } from "../MegarideEventDispatch.js";
import { MegaridePlayerControls, ParthenopePlayer } from "../MegaridePlayer.js";
import { ComscoreLoadMode, ParthenopePlayerConfig } from "../MegaridePlayerConfig.js";
import { MegaridePlayerControlsBaseImpl, MegaridePlayerControlsConfig } from "../MegaridePlayerControlsBaseImpl.js";
import { getAdvConfiguration } from "../ads/AdvConfig.js";
import { fetchABRStreamingEngine } from "../modules/ABRStreamingEngineModule.js";
import { MegarideCSStreamingTagMapper } from "../stats/mappers/MegarideCSStreamingTagMapper.js";
import { populateAuthorPlaceholder, populateViewsPlaceholder } from "../utils/YMutils.js";
import { ImaControllerBuilder } from "./builders/ImaControllerBuidler.js";
import { PrebidBuilder } from "./builders/PrebidBuilder.js";
import { ResolverBuilder } from "./builders/ResolverBuilder.js";
import { StatsMapperBuilder } from "./builders/StatsMapperBuilder.js";
import { ParthenopeGUIV2 } from "./gui/ParthenopeGUIV2.js";
import { PreloadReleaseMode } from "@mp-commons/modules/player/PreloadReleaseMode.js";
import { PlayerTechFactoryConfig } from "@mp-commons/modules/player/PlayerTechFactory.js";
import { VideoPlaylist } from "@mp-commons/model/video/VideoPlaylist.js";
import { VideoContainerType } from "@mp-commons/model/video/VideoFormat.js";
import { VideoInfoRawData } from "@mp-commons/model/video/VideoInfo.js";
import { MediaAudioOnPolicy, MediaAutoplayPolicy } from "@mp-commons/config/MediaPlayerConfig.js";

const PLAYERNAME = "ParthenopePlayerV3";



export class ParthenopePlayerV3 implements ParthenopePlayer {

    private readonly name: string = PLAYERNAME


    static abrStreamingMgmtBuild(playlist: VideoPlaylist,
        config: ParthenopePlayerConfig,
        rootElement: HTMLElement = null,
        window: Window
    ): Promise<ParthenopePlayerV3> {
        const cPlaylist = new StaticPlaylist<VideoInfoRawData>(playlist);
        const gui = new ParthenopeGUIV2(window, rootElement, config);

        const unmuter = new NoOpUnumuter();

        const playerOptions: MegaridePlayerControlsConfig = {
            events: {
                enable: false,
                dynamic_viewable_impression: true,
                dynamic_viewable_impression_cap: 5,
                progress_resolution: 1
            },
            autoplay: config.embeddablePlayerConfig?.autoplayPolicy || MediaAutoplayPolicy.AUTOPLAY_ALWAYS,
            loopPlaylist: config.embeddablePlayerConfig?.loopPlaylist || false,
            usePreferenceQuality: false,
            audioOnPolicy: MediaAudioOnPolicy.OFF,

        }

        const factoryConfig: PlayerTechFactoryConfig = {
            enableGC: false,
            unloadManagementStrategy: UnloadManagementStrategy.REMOVE_PRELOADED_RESOURCE,
            preloadManagementStrategy: PreloadManagementStrategy.NO_CLEANUP,
            playOptions: {
                audioMode: AudioMode.SELECTABLE_AUDIO,
                playMode: PlayMode.SINGLE_PLAY,
                timeCutSeconds: -1
            },
            enableReusingMediaResource: false,
            preloadReleaseMode: PreloadReleaseMode.RELEASE_ON_PLAYER_READY,
        }


        const adControllerPromise = Promise.resolve(new NullAdsController())

        const controlsPromise = fetchABRStreamingEngine().then(engine => engine(cPlaylist,
            gui,
            playerOptions,
            factoryConfig,
            adControllerPromise,
            unmuter, window));

        return controlsPromise.then(ctrl => {
            return new ParthenopePlayerV3(ctrl, gui, config, window)
        });
    }


    static managementBuild(playlist: VideoPlaylist,
        config: ParthenopePlayerConfig,
        rootElement: HTMLElement = null,
        window: Window
    ): Promise<ParthenopePlayerV3> {

        const cPlaylist = new StaticPlaylist<VideoInfoRawData>(playlist);
        const gui = new ParthenopeGUIV2(window, rootElement, config);



        const factoryBuilder = (ctrl: VideoPlayerControls) => {
            const factory = new NativeVODPlayerFactory(ctrl, window, {
                enableGC: false,
                preloadReleaseMode: PreloadReleaseMode.RELEASE_ON_PLAYER_READY,
                unloadManagementStrategy: UnloadManagementStrategy.REMOVE_PRELOADED_RESOURCE,
                preloadManagementStrategy: PreloadManagementStrategy.NO_CLEANUP,
                playOptions: {
                    audioMode: AudioMode.SELECTABLE_AUDIO,
                    playMode: PlayMode.SINGLE_PLAY,
                    timeCutSeconds: -1
                }, enableReusingMediaResource: false,
                selectedContainer: VideoContainerType.PROGRESSIVE_MP4,
                loadStrategy: NativeVODLoadStrategy.LAZY_LOAD
            });

            factory.setSizePreferencePolicy(createSizePreference({
                size: 360, //replace with setting from user preferences
                fallback: "lower",
                dimension: "significant"
            }));


            return factory;

        }

        // const factory = new NativeVODPlayerFactory(window, VideoContainerType.PROGRESSIVE_MP4, NativeVODLoadStrategy.LAZY_LOAD);
        // factory.setSizePreferencePolicy(createSizePreference({
        //     size: 360, //replace with setting from user preferences
        //     fallback: "lower",
        //     dimension: "significant"
        // }));


        const unmuter = new NoOpUnumuter();

        const playerOptions: MegaridePlayerControlsConfig = {

            events: {
                enable: false,
                dynamic_viewable_impression: true,
                dynamic_viewable_impression_cap: 5,
                progress_resolution: 1
            },
            autoplay: config.embeddablePlayerConfig?.autoplayPolicy || MediaAutoplayPolicy.AUTOPLAY_IF_ADS_ARE_SERVABLE,
            loopPlaylist: config.embeddablePlayerConfig?.loopPlaylist || false,
            usePreferenceQuality: false,
            audioOnPolicy: MediaAudioOnPolicy.OFF
        }


        const adControllerPromise = Promise.resolve(new NullAdsController())

        const controlsPromise = Promise.resolve(new MegaridePlayerControlsBaseImpl(
            PLAYERNAME, cPlaylist, factoryBuilder, adControllerPromise, gui, playerOptions, unmuter, window))

        return controlsPromise.then(ctrl => {
            return new ParthenopePlayerV3(ctrl, gui, config, window)
        });

    }

    static build(playlist: VideoPlaylist,
        adUnitsConfig: VASTAdUnitConfig[],
        config: ParthenopePlayerConfig,
        extData: ExternallyPassedContentAdData = null,
        rootElement: HTMLElement = null,
        window: Window
    ): Promise<ParthenopePlayerV3> {

        const wvDetect: DetectedWebView = detectWebView();
        const useComscoreLoadMode: ComscoreLoadMode = config.comscoreLoadMode ? config.comscoreLoadMode : "jit";
        const enableComscore = true;

        const cPlaylist = new StaticPlaylist<VideoInfoRawData>(playlist);
        const gui = new ParthenopeGUIV2(window, rootElement, config);
        const useVideoPolicy = config.videoPolicy ? config.videoPolicy : VideoPolicyRules.STANDARD;

        const unmuter = new BaseUnmuter(gui.rootEl, gui.getVideoElement().mediaItem);
        const userData = new UserAdsData(window);

        const stats = StatsMapperBuilder(window, extData.embedType, {
            ga4: {
                enable: true,
                media: false,
                aderrors: false,
                adevents: false,
                media_progress: false
            },
            ymstats: {
                enable: true,
                media: true,
                media_progress: true,
                adevents: true,
                aderrors: true
            },
            nielsen: {
                enable: true
            },
            comscore: {
                enable: enableComscore,
            },
            playerName: "megaride"
        }, extData)


        let maybeComscorePromise: Promise<void> = Promise.resolve();
        if (enableComscore) {
            console.log("COMSCORE load mode is " + useComscoreLoadMode);
            if (useComscoreLoadMode != "jit") {
                const maybeComscore = stats.find((el) => el instanceof MegarideCSStreamingTagMapper);
                if (maybeComscore && maybeComscore instanceof MegarideCSStreamingTagMapper) {
                    console.log("COMSCORE pre-scheduling module download");
                    const pPromise = maybeComscore.preload();
                    if (useComscoreLoadMode == "preload") {
                        console.log("COMSCORE await module set");
                        maybeComscorePromise = pPromise;
                    }
                }
            }
        }


        const factoryBuilder = (ctrl: VideoPlayerControls) => {
            const factory = new NativeVODPlayerFactory(ctrl, window, {
                enableGC: false,
                preloadReleaseMode: PreloadReleaseMode.RELEASE_ON_PLAYER_READY,
                unloadManagementStrategy: UnloadManagementStrategy.REMOVE_PRELOADED_RESOURCE,
                preloadManagementStrategy: PreloadManagementStrategy.NO_CLEANUP,
                enableReusingMediaResource: false,
                playOptions: {
                    audioMode: AudioMode.SELECTABLE_AUDIO,
                    playMode: PlayMode.SINGLE_PLAY,
                    timeCutSeconds: -1
                },
                selectedContainer: VideoContainerType.PROGRESSIVE_MP4,
                loadStrategy: NativeVODLoadStrategy.LAZY_LOAD
            });
            const useSize: () => number = () => {

                // if (config.embeddablePlayerConfig?.usePreferenceQuality) {
                //     const size_preference = localStorage.getItem(QUALITY_PREFERENCE_KEY);
                //     if (size_preference) {
                //         const res = parseInt(size_preference);
                //         if (!isNaN(res))
                //             return res;
                //     }
                // }

                let startingQuality = 360;
                switch (extData.embedType) {
                    case CP_EmbedType.VIDEO_TINT:
                    case CP_EmbedType.FANPLAYER:
                        startingQuality = 240;
                        break;
                    case CP_EmbedType.VIDEO_TESTO:
                        startingQuality = wvDetect.device?.other ? 480 : 360;
                        break;
                }
                return startingQuality;
            };

            factory.setSizePreferencePolicy(createSizePreference({
                size: useSize(),
                fallback: "lower",
                dimension: "significant"
            }));

            return factory;

        }

        const playerOptions: MegaridePlayerControlsConfig = {
            events: {
                enable: true,
                dynamic_viewable_impression: true,
                dynamic_viewable_impression_cap: 5,
                progress_resolution: 1
            },
            autoplay: config.embeddablePlayerConfig?.autoplayPolicy || MediaAutoplayPolicy.AUTOPLAY_ALWAYS,
            loopPlaylist: config.embeddablePlayerConfig?.loopPlaylist || false,
            usePreferenceQuality: false, //config.embeddablePlayerConfig?.usePreferenceQuality || false,
            audioOnPolicy: config.audioOnPolicy
        }




        if (config.withoutAdvertising) {
            console.log("Without Ads - not downloading IMA");
        }
        else console.log("Downloading IMA");
        //const adsController: AdsController = new NullAdsController();

        const adControllerPromise = config.withoutAdvertising ? Promise.resolve(new NullAdsController()) : (ImaControllerBuilder((ima, gdpr) => {

            const baseOpts: MultipleVASTImaAdCallControllerConfig =
                getAdvConfiguration(useVideoPolicy, extData.embedType, wvDetect, true, adUnitsConfig);


            const contentSpecificAdData = new ContentSpecificAdData(window, extData, null)

            const prebid = config.prebid?.enable ? PrebidBuilder(window, userData,
                config.prebid?.amazon ? { pubId: config.prebid?.amazon.pubID, behaviour: AmazonBidBehaviour.CHECK_FOR_EXPORTED_BIDS } : null,
                config.prebid?.ias, {enabled: true, site : contentSpecificAdData.site},{enabled: true},config.prebid?.timeout) : new NullPrebidModule();

            //const userGestureUnmuter = extData.embedType == CP_EmbedType.VIDEO_TINT ? new NullUserGestureUnmuter() : new ImaUserGestureUnmuterMulinoBiancoImpl();


            const externalUIElements: HasExternalUIElements = {
                getCloseExternalUIElement: () => {
                    return gui.getCloseExternalUIElement();
                }
            }

            //#12431 revisione unmute policy
            // const imaUnmuter = config.audioOnPolicy != MegarideAudioOnPolicy.ON ?
            //     new IMAAdvancedUnmuter(unmuter, gui.getVideoElement().videoItem, externalUIElements, wvDetect) :
            //     new IMAFollowVideoStatusUnmuter(gui.getVideoElement().videoItem, externalUIElements)
            const imaUnmuter = new IMAAdvancedUnmuter(unmuter, gui.getVideoElement().mediaItem, externalUIElements, wvDetect, IMAUnmuterConservativeConfig);
            const resolver = ResolverBuilder(window, useVideoPolicy, contentSpecificAdData, unmuter, imaUnmuter, extData, userData, false);

            return new MultipleVASTImaAdCallController(ima,
                window,
                gdpr,
                userData,
                prebid,
                baseOpts,
                resolver,
                gui.getAdContainer(),
                gui.getVideoElement().mediaItem,
                imaUnmuter
            )
        }, false))

        const controlsPromise = Promise.resolve(new MegaridePlayerControlsBaseImpl(
            PLAYERNAME, cPlaylist, factoryBuilder, adControllerPromise, gui, playerOptions, unmuter, window))

        return controlsPromise.then(ctrl => {
            stats.forEach(s => s.attachEventSource(ctrl));
            return maybeComscorePromise.then(_ => {
                return new ParthenopePlayerV3(ctrl, gui, config, window)
            })
        });

    }


    private constructor(private readonly controls: MegaridePlayerControls,
        private readonly gui: ParthenopeGUIV2,
        private readonly config: ParthenopePlayerConfig,
        private window: Window
    ) {


        dispatchMegarideEvents(this, window, false)

    }

    get headEl(): HTMLElement {
        return this.gui.headEl;
    }

    get guid(): string {
        return this.gui.rootEl?.id;
    }

    private getInitialVolume(): number {
        const configVolume = this.config?.embeddablePlayerConfig?.volume;
        let initalVolume = 0.5;
        if (isFinite(configVolume) && configVolume >= 0) {
            initalVolume = configVolume / 100;
        }
        return initalVolume
    }

    playNext(forceResume: boolean): Promise<void> {
        return this.controls.playNext(forceResume).then();
    }

    subscribeMediaStatus(fn: (s: MediaState) => void): MediaStatusSubscription {
        return this.controls.subscribeMediaStatus(fn)
    }

    getControls(): MegaridePlayerControls {
        return this.controls;
    }
    getGUI(): ParthenopeGUIV2 {
        return this.gui;
    }
    getPlayerName(): string {
        return this.name;
    }
    init(): Promise<void> {
        return this.controls.init().then(ctx => {
            populateAuthorPlaceholder(ctx.ref.info, this.config.embeddablePlayerConfig?.authorPlaceholder, this.window)
            populateViewsPlaceholder(ctx.ref.info, this.config.embeddablePlayerConfig?.viewsPlaceholder, this.window)

            ctx.volume(this.getInitialVolume());
        });
    }

    start(): Promise<void> {
        return this.controls.start().then();
    }
    anchorageUpdate(anchored: boolean): void {
        this.gui.anchorageUpdate(anchored)
    }

    setExternalCloseUIElement(el: ExternalCloseUIElement): void {
        this.gui.setExternalCloseUIElement(el);
    }

    setFullscreen(fullscreen: boolean): void {
        this.gui.setFullscreen(fullscreen);
    }

}
